<template>
    <div class="results">
        <h1 class="quiz__title">Your Results</h1>

        <div class="results__header">
            <h1>Ultimune Beauty Regimen</h1>
            <p>Our core skincare routine includes 3 simple, but powerful steps to reveal your most beautiful skin.</p>
        </div>

        <div class="regimen">
            <section class="regimen__step --step-1">
                <p class="regimen__instructions">
                    <span class="ff--rb">Step 1 STRENGTHEN:</span>
                    <br>
                    <span>Prevent and pretreat your face and eyes against visible signs of aging.</span>
                    <br>
                    <span>After cleansing, apply two pumps morning and night before your moisturizer for enhanced results.</span>
                </p>

                <div class="regimen__products">
                    <product-view v-if="products[0]" :upc="products[0]" cat="Strengthen" />
                    <product-view v-if="products[1]" :upc="products[1]" cat="Strengthen: Eye" />
                </div>
            </section>

            <section class="regimen__step --step-2" v-if="moisturizing">
                <p class="regimen__instructions">
                    <span class="ff--rb">Step 2 MOISTURIZE:</span>
                    <br>
                    <span>Hydrate your skin and lock in nutrients with a day and night moisturizer and eye cream based on your skin concern.</span>
                    <br>
                    <span>Smooth evenly over face and delicate eye area after cleansing and strengthening your skin.</span>
                </p>

                <div class="regimen__products" v-if="products[2] === products[3]">
                    <product-view @not-found="mark(2)" v-if="products[2]" :upc="products[2]" cat="Moisturize" />
                    <product-view @not-found="mark(4)" v-if="products[4]" :upc="products[4]" cat="Eye Treatment" />
                </div>
                <div class="regimen__products" v-else>
                    <product-view @not-found="mark(2)" v-if="products[2]" :upc="products[2]" :cat="moist_text(2)" />
                    <product-view @not-found="mark(3)" v-if="products[3]" :upc="products[3]" :cat="moist_text(3)" />
                    <product-view @not-found="mark(4)" v-if="products[4]" :upc="products[4]" cat="Eye Treatment" />
                </div>
            </section>

            <section class="regimen__step --step-3" v-if="protection">
                <p class="regimen__instructions">
                    <span class="ff--rb">Step 3 PROTECT:</span>
                    <br>
                    <span>Shield skin with SPF after moisturizing to protect from UV damage and pollutants.</span>
                    <br>
                    <span>Apply a generous amount evenly to face.</span>
                </p>

                <div class="regimen__products">
                    <product-view @not-found="mark(5)" :upc="products[5]" prot sub-text="For everyday sunprotection. Broad Spectrum SPF 42" />
                </div>
            </section>
        </div>

        <div class="prep" v-if="prepared">
            <div class="prep__instructions">
                <p>Enhance your skincare routine when you begin with cleansing and balancing your skin and applying a targeted treatment before moisturizing.</p>
            </div>

            <div class="prep__products">
                <product-view @not-found="mark(6)" v-if="products[6]" :upc="products[6]" cat="Cleanse" sub-text="Remove impurities and makeup as the first step in your skincare routine" />
                <product-view @not-found="mark(7)" v-if="products[7]" :upc="products[7]" cat="Soften" sub-text="Applying this liquid essence after you cleanse, before your pre-treatment, makes even the driest skin feel hydrated all day" />
                <product-view @not-found="mark(8)" v-if="products[8]" :upc="products[8]" cat="Correct" sub-text="Applied after softening the skin, lightweight serums have the most active ingreidents that briing out your most beautiful skin" />
            </div>
        </div>

        <div class="treat-yourself" v-if="treats">
            <h1 class="treat-yourself__header">Treat Yourself</h1>

            <div class="treat-yourself__products">
                <product-view @not-found="mark(9)" v-if="products[9]" no-min-height :upc="products[9]" />
                <product-view @not-found="mark(10)" v-if="products[10]" no-min-height :upc="products[10]" />
            </div>
        </div>

        <div class="regimen__add-all-to-bag" v-show="price > 0">
            <h1>Add the complete regimen for ${{ price | thousands }}</h1>
            <button class="button" @click="addAllToBag">ADD ALL TO BAG</button>
        </div>

        <div class="regimen__ctas">
            <div class="regimen__save-my-results">
                <save-results />
                <button @click="saveRecommendationsForLater" class="save-btn">{{ save_reco_text }}</button>
                <a class="link" @click="startOver">START OVER</a>
            </div>

            <div class="regimen__further-help">
                <a href="/concierge-form.html" target="_blank">
                    <img :src="contentAssetPath('images/beauty-consultant.jpg')">
                </a>

                <img :src="contentAssetPath('images/shiseido-icon.png')" alt="">

                <a href="/foundation-finder.html" target="_blank">
                    <img :src="contentAssetPath('images/find-your-ideal-skin.jpg')">
                </a>
            </div>
        </div>
        <transition name="fade" mode="out-in">
            <light-box v-show="show_lightbox" @close="close_lightbox" text="Your recommendations have been successfully saved to your wishlist" />
        </transition>
    </div>
</template>

<script>
import logic from '../data/logic'
import { bus, cookie_key, contentAssetPath } from '../utilities'
import { shopping, user } from 'shiseido-apis'
import SaveResults from './btn_save-results.vue'
import Product from './product.vue'
import LightBox from './lightbox.vue'

export default {
    name: 'Results',
    components: {
        'product-view': Product,
        SaveResults,
        LightBox,
    },
    filters: {
        thousands(number) {
            return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        },
    },
    data() {
        return {
            products: [],
            prices: [],
            found: [true, true, true, true, true, true, true, true, true, true, true],
            show_lightbox: false,
        }
    },
    computed: {
        price() {
            return this.prices.reduce((a,b) => a + b, 0)
        },
        save_reco_text() {
            return window.innerWidth < 768 ? 'SAVE RECOMMENDATIONS' : 'SAVE MY RECOMMENDATIONS TO MY ACCOUNT'
        },
        moisturizing() {
            return [2,3,4].some((i) => this.products[i]) && [2,3,4].some((i) => this.found[i])
        },
        protection() {
            return this.products[5] && this.found[5]
        },
        prepared() {
            return [6,7,8].some((i) => this.products[i]) && [6,7,8].some((i) => this.found[i])
        },
        treats() {
            return [9,10].some((i) => this.products[i]) && [9,10].some((i) => this.found[i])
        },
    },
    created() {
        this.$cookie.delete(cookie_key)
        bus.$on('product::found_orderable', ({ price }) => {
            this.prices.push(price)
        })

        const { gender, age, life_style, skin_type, skin_concern, benefit } = this.$route.params
        const concern = skin_concern.length > 2 ? '10' : skin_concern
        const regimen_set_identifier = logic.results[gender][age][life_style][skin_type][concern][benefit]
        const regimen_set = logic.regimenSets[regimen_set_identifier]

        for (let i = 0; i < regimen_set.length; i++) {
            const upc = logic.regimenSkus[regimen_set[i]]
            if (typeof upc === 'undefined') {
                this.mark(i)
            }
            this.products.push(upc)
        }

        if (this.$route.params.saved === 'saved') {
            shopping.addToWishlist(this.products_no_repeated_upcs())

            window.location.href = window.location.href.replace(/\/saved\/?$/, '')

            this.display_lightbox()
        }
    },
    methods: {
        addAllToBag() {
            shopping.addToCart(this.products_no_repeated_upcs())
        },
        startOver() {
            bus.$emit('next', '/')
            window.scrollTo(0, document.getElementById('composed__shiseido-skincare-consultation').offsetTop)
        },
        saveRecommendationsForLater() {
            if (user.loggedIn()) {
                shopping.addToWishlist(this.products_no_repeated_upcs())
                
                return this.display_lightbox()
            }

            this.setHashPath()
            shopping.addToWishlist(this.products_no_repeated_upcs())
        },
        products_no_repeated_upcs() {
            // Some results have day/night moisturize as the same product
            const is_unique = (value, index, self) => self.indexOf(value) === index
            // Not all entries are defined
            const is_truthy = (p) => p

            return this.products
                .filter(is_truthy)
                .filter(is_unique)
        },
        setHashPath() {
            const curr_path = this.$route.path.replace(/\/$/, '')
            const hash_path = window.location.origin
                .concat(`?hashpath=${ curr_path }/#${ curr_path }/saved`)

            window.history.pushState({}, '', hash_path)
        },
        moist_text(id) {
            if (id === 2) {
                return this.found[3] ? 'Moisturize: Daytime' : 'Moisturize'
            }
            if (id === 3) {
                return this.found[2] ? 'Moisturize: Nighttime' : 'Moisturize'
            }
        },
        mark(id) {
            const temp = [...this.found]
            temp[id] = false
            this.found = temp
        },
        display_lightbox() {
            this.show_lightbox = true
            setTimeout(() => {
                this.show_lightbox = false
            }, 3000)
        },
        close_lightbox() {
            this.show_lightbox = false
        },
        contentAssetPath,
    },
}
</script>


// WEBPACK FOOTER //
// source/app/js/components/results.vue